import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Fingerprint, ScanFace, Lock, AlertCircle, LogOut, Loader2,
  AlertTriangle, Phone, Heart, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppLock } from '@/contexts/AppLockContext';
import { useAuth } from '@/contexts/AuthContext';
import useBiometricAuth from '@/hooks/useBiometricAuth';
import AppLogo from '@/components/AppLogo';

/**
 * AppLockScreen - Biometric unlock screen
 * 
 * Shows when app is locked and requires biometric/passcode authentication.
 * Features:
 * - Biometric/passcode unlock
 * - Emergency quick-access button (bypasses lock for emergencies)
 * - Logout option
 * 
 * Design: Simple, secure, low-friction with calming pastel aesthetics.
 */
const AppLockScreen = () => {
  const navigate = useNavigate();
  const { unlock } = useAppLock();
  const { logout, user } = useAuth();
  const { 
    isSupported, 
    isEnrolled, 
    isAuthenticating, 
    error, 
    authenticate 
  } = useBiometricAuth();
  
  const [authError, setAuthError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showEmergencyAccess, setShowEmergencyAccess] = useState(false);
  const [emergencyCountdown, setEmergencyCountdown] = useState(null);

  const handleBiometricAuth = useCallback(async () => {
    setAuthError(null);
    
    try {
      const success = await authenticate();
      if (success) {
        unlock();
      }
    } catch (err) {
      setAuthError(err.message || 'Authentication failed');
      setAttempts(prev => prev + 1);
    }
  }, [authenticate, unlock]);

  const handleEmergencyAccess = useCallback(() => {
    // Bypass lock and go directly to emergency mode
    setEmergencyCountdown(null);
    setShowEmergencyAccess(false);
    // Navigate directly to emergency mode without unlocking full app
    window.location.href = '/emergency-mode';
  }, []);

  // Auto-trigger biometric on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isSupported && isEnrolled) {
        handleBiometricAuth();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [isSupported, isEnrolled, handleBiometricAuth]);

  // Emergency countdown timer
  useEffect(() => {
    if (emergencyCountdown === null) return;
    
    if (emergencyCountdown <= 0) {
      // Navigate to emergency mode
      handleEmergencyAccess();
      return;
    }
    
    const timer = setTimeout(() => {
      setEmergencyCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [emergencyCountdown, handleEmergencyAccess]);

  const handleLogout = async () => {
    await logout();
    setShowLogoutConfirm(false);
  };

  const startEmergencyCountdown = () => {
    setEmergencyCountdown(3);
  };

  const cancelEmergencyCountdown = () => {
    setEmergencyCountdown(null);
    setShowEmergencyAccess(false);
  };

  // Get the appropriate icon based on platform
  const getBiometricIcon = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      return <ScanFace className="h-12 w-12 text-sky-500" />;
    }
    return <Fingerprint className="h-12 w-12 text-sky-500" />;
  };

  const getBiometricLabel = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      return 'Face ID / Passcode';
    }
    return 'Fingerprint / PIN';
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-gradient-to-b from-sky-50 via-white to-rose-50 flex flex-col items-center justify-center p-6"
      data-testid="app-lock-screen"
    >
      {/* Emergency Access Modal */}
      <AnimatePresence>
        {showEmergencyAccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={cancelEmergencyCountdown}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  {emergencyCountdown !== null ? (
                    <span className="text-3xl font-bold text-red-600">{emergencyCountdown}</span>
                  ) : (
                    <AlertTriangle className="h-8 w-8 text-red-600" />
                  )}
                </div>
                
                <h3 className="text-xl font-semibold text-slate-800 mb-2">
                  Emergency Access
                </h3>
                
                <p className="text-slate-500 text-sm mb-6">
                  This will show critical medical information without requiring full unlock.
                  <span className="block mt-2 text-red-600 font-medium">
                    For emergencies only.
                  </span>
                </p>
                
                {emergencyCountdown === null ? (
                  <div className="space-y-3">
                    <Button
                      className="w-full h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl"
                      onClick={startEmergencyCountdown}
                      data-testid="confirm-emergency-btn"
                    >
                      <Heart className="h-5 w-5 mr-2" />
                      Show Emergency Info
                    </Button>
                    
                    <Button
                      variant="ghost"
                      className="w-full text-slate-500"
                      onClick={cancelEmergencyCountdown}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-lg text-slate-600">
                      Opening in <span className="font-bold text-red-600">{emergencyCountdown}</span> seconds...
                    </p>
                    
                    <Button
                      variant="outline"
                      className="w-full border-slate-300"
                      onClick={cancelEmergencyCountdown}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logo and branding */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="mb-8 text-center"
      >
        <AppLogo size="lg" showTagline={false} />
        <p className="text-slate-500 text-sm mt-2">Your information is protected</p>
      </motion.div>

      {/* Lock indicator */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mb-8"
      >
        <div className="w-24 h-24 rounded-full bg-white shadow-lg flex items-center justify-center border-4 border-sky-100">
          {isAuthenticating ? (
            <Loader2 className="h-10 w-10 text-sky-500 animate-spin" />
          ) : (
            <Lock className="h-10 w-10 text-sky-500" />
          )}
        </div>
      </motion.div>

      {/* User greeting */}
      {user && (
        <motion.p
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-lg text-slate-700 mb-2"
        >
          Welcome back, <span className="font-semibold">{user.name?.split(' ')[0] || 'there'}</span>
        </motion.p>
      )}

      {/* Instructions */}
      <motion.p
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-slate-500 text-center mb-8 max-w-xs"
      >
        {isSupported && isEnrolled 
          ? 'Unlock with biometrics to access your information'
          : 'Tap below to unlock the app'}
      </motion.p>

      {/* Error message */}
      <AnimatePresence>
        {authError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6 flex items-center gap-2 px-4 py-3 bg-red-50 text-red-600 rounded-xl text-sm"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{authError}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Biometric button */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="space-y-4 w-full max-w-xs"
      >
        {isSupported ? (
          <Button
            onClick={handleBiometricAuth}
            disabled={isAuthenticating}
            className="w-full h-14 bg-sky-500 hover:bg-sky-600 text-white rounded-2xl shadow-lg shadow-sky-200/50 flex items-center justify-center gap-3"
            data-testid="unlock-button"
          >
            {isAuthenticating ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Authenticating...</span>
              </>
            ) : (
              <>
                {getBiometricIcon()}
                <span className="text-base font-medium">
                  Unlock with {getBiometricLabel()}
                </span>
              </>
            )}
          </Button>
        ) : (
          <Button
            onClick={unlock}
            className="w-full h-14 bg-sky-500 hover:bg-sky-600 text-white rounded-2xl shadow-lg shadow-sky-200/50"
            data-testid="unlock-button"
          >
            <Lock className="h-5 w-5 mr-2" />
            <span>Unlock App</span>
          </Button>
        )}

        {/* Emergency Access Button */}
        <Button
          variant="outline"
          className="w-full h-12 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl"
          onClick={() => setShowEmergencyAccess(true)}
          data-testid="emergency-access-button"
        >
          <AlertTriangle className="h-5 w-5 mr-2" />
          <span>Emergency Access</span>
        </Button>

        {/* Retry hint after multiple failures */}
        {attempts >= 3 && (
          <p className="text-center text-sm text-slate-400">
            Having trouble? Try using your device passcode instead.
          </p>
        )}
      </motion.div>

      {/* Logout option */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="absolute bottom-8"
      >
        {showLogoutConfirm ? (
          <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-md">
            <span className="text-sm text-slate-600">Sign out?</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowLogoutConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleLogout}
            >
              Sign Out
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            className="text-slate-400 hover:text-slate-600"
            onClick={() => setShowLogoutConfirm(true)}
            data-testid="logout-from-lock-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Use a different account
          </Button>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AppLockScreen;
